import { useContext } from "react";
import { filteredQuestionIds } from "../../../Common/CommonFunctions/filterQuestionIds";
import { ActionKind } from "../../../SupplierView/SupplierViewPropsTypes";
import { CheckBoxPropsTypes } from "../CheckBoxPropsTypes";
import CheckBox from "../Components/CheckBox";
import CheckBoxMobileView from "../Components/CheckBoxMobileView";
import { CheckResponsiveContext } from "../../../Context/CheckResponsiveContext";

function CheckBoxContainer({
  surveyAnswers,
  setSurveyAnswers,
  questionObjVariable,
  questionId,
  mainRefObject,
  checkNestedDependency,
  isPreview,
  dependencyAnsHash,
  setDependencyAnsHash,
  clearValuesFromSurveyAnswers,
  dispatch,
  reducerState,
  surveyAnswersInfo,
}: CheckBoxPropsTypes) {
  const { isMobile } = useContext(CheckResponsiveContext);
  const othersOption =
    questionObjVariable?.[questionId]?.optionInfo?.optionObj[
      questionObjVariable?.[questionId]?.optionInfo?.optionOrder[
        questionObjVariable?.[questionId]?.optionInfo?.optionOrder.length - 1
      ]
    ];
  // func to calculate overall score for checkboxes selected
  function calculateOverallScoreForMulti(
    questionId: string,
    weightage: number,
    surveyAnswersCopy: CheckBoxPropsTypes["surveyAnswers"]
  ) {
    if (!surveyAnswersCopy[questionId]["score"]) {
      surveyAnswersCopy[questionId]["score"] = Number(weightage);
    } else {
      surveyAnswersCopy[questionId]["score"]! += Number(weightage);
    }
  }
  // function for handling checked state of checkbox
  function onCheckedState(
    questionId: string,
    name: string,
    optionId: string,
    weightage: number,
    surveyAnswersCopy: CheckBoxPropsTypes["surveyAnswers"],
    dependencyAnsHashCopy: Set<string>,
    isScoreEnable: boolean
  ) {
    surveyAnswersCopy[questionId] = surveyAnswersCopy[questionId] || {};
    surveyAnswersCopy[questionId]["val"] =
      surveyAnswersCopy[questionId]["val"] || {};
    if (
      questionObjVariable?.[questionId]?.enableOthers &&
      optionId === othersOption.optionId
    ) {
      surveyAnswersCopy[questionId].isOthersChecked = true;
    }
    surveyAnswersCopy[questionId]["val"][optionId] = {
      optionId,
      optionName: name,
      ...(isScoreEnable && { score: weightage }),
    };
    //dependency part
    //if question has no children dont do the below steps
    if (
      questionObjVariable?.[questionId]?.optionInfo?.optionObj?.[optionId]
        .dependentQuestionIds.length
    ) {
      dependencyAnsHashCopy.add(optionId);

      // store option ids of current question in map
      if (
        mainRefObject!.current.questionOptionsInfoRef.current.has(questionId)
      ) {
        mainRefObject!.current.questionOptionsInfoRef.current
          .get(questionId)
          ?.selectedoptionsIds.add(optionId);
      } else {
        mainRefObject!.current.questionOptionsInfoRef.current.set(questionId, {
          selectedoptionsIds: new Set([optionId]),
        });
      }

      setDependencyAnsHash!(dependencyAnsHashCopy);
    }
  }

  //checkbox main func
  const handleMultiChange: CheckBoxPropsTypes["handleMultiChange"] = (
    e,
    questionId,
    optionId,
    name,
    weightage
  ) => {
    let surveyAnswersCopy = structuredClone(surveyAnswers);
    let dependencyAnsHashCopy = new Set(dependencyAnsHash);
    if (e.target.checked) {
      onCheckedState(
        questionId,
        name,
        optionId,
        weightage,
        surveyAnswersCopy,
        dependencyAnsHashCopy,
        surveyAnswersInfo.surveyInfo.isScoreEnable
      );
      if (surveyAnswersInfo.surveyInfo.isScoreEnable) {
        calculateOverallScoreForMulti(questionId, weightage, surveyAnswersCopy);
      }
      setSurveyAnswers!(surveyAnswersCopy);
    } else {
      //  case: uncheck
      delete surveyAnswersCopy[questionId]["val"][optionId];
      if (surveyAnswersInfo.surveyInfo.isScoreEnable) {
        surveyAnswersCopy[questionId]["score"]! -= Number(weightage);
      }
      if (
        questionObjVariable?.[questionId]?.enableOthers &&
        optionId === othersOption.optionId
      ) {
        surveyAnswersCopy[questionId].isOthersChecked = false;
        delete surveyAnswersCopy[questionId].val[optionId];
      }

      if (
        !Object.keys?.(surveyAnswersCopy?.[questionId]?.val)?.length &&
        surveyAnswersCopy?.[questionId]?.uploads?.length
      )
        delete surveyAnswersCopy[questionId]["score"];
      if (
        !Object.keys(surveyAnswersCopy?.[questionId]?.val)?.length &&
        !surveyAnswersCopy?.[questionId]?.uploads?.length &&
        !surveyAnswersCopy?.[questionId]?.others?.enabled &&
        !surveyAnswersCopy?.[questionId]?.comment
      ) {
        clearValuesFromSurveyAnswers!(questionId, surveyAnswersCopy);
        // clearAllFiles!(questionId);
      }

      //dependency part
      //if question has no children dont do the below steps
      if (
        questionObjVariable?.[questionId]?.optionInfo?.optionObj?.[optionId]
          .dependentQuestionIds.length
      ) {
        dependencyAnsHashCopy.delete(optionId);

        mainRefObject!.current.questionOptionsInfoRef.current
          .get(questionId)
          ?.selectedoptionsIds.delete(optionId);
        mainRefObject!.current.questionOptionsInfoRef.current.get(questionId)
          ?.selectedoptionsIds.size === 0 &&
          mainRefObject!.current.questionOptionsInfoRef.current.delete(
            questionId
          );
        checkNestedDependency!(
          questionObjVariable?.[questionId]?.optionInfo.optionObj[optionId]
            .dependentQuestionIds,
          surveyAnswersCopy,
          dependencyAnsHashCopy
        );
        setDependencyAnsHash!(dependencyAnsHashCopy);
        // setQuestionIdsToRender!([...filteredQuestionIds!]);
      }
    }
    if (
      questionObjVariable?.[questionId]?.optionInfo?.optionObj?.[optionId]
        .dependentQuestionIds.length
    ) {
      // filter out questions tht are dependent, and only keep questions to render
      const updatedFilterData = filteredQuestionIds(
        reducerState!.allQuestionIds,
        questionObjVariable,
        dependencyAnsHashCopy
      );
      dispatch!({
        type: ActionKind.QUESTION_IDS_TO_RENDER,
        payload: { updatedFilterData: updatedFilterData },
      });
    }
    setSurveyAnswers!(surveyAnswersCopy);
  };

  //handle change for other options text area
  const handleChangeOtherOptionTextArea: CheckBoxPropsTypes["handleChangeOtherOptionTextArea"] =
    (event, othersOptionId) => {
      const { value } = event.target;
      let surveyAnswersCopy = structuredClone(surveyAnswers);
      surveyAnswersCopy[questionId].val[othersOptionId]["othersAnswer"] = value;
      setSurveyAnswers!(surveyAnswersCopy);
    };
  return (
    <>
      {isMobile ? (
        <CheckBoxMobileView
          handleMultiChange={handleMultiChange}
          questionObjVariable={questionObjVariable}
          questionId={questionId}
          surveyAnswers={surveyAnswers}
          isPreview={isPreview}
          surveyAnswersInfo={surveyAnswersInfo}
          handleChangeOtherOptionTextArea={handleChangeOtherOptionTextArea}
          othersOption={othersOption}
        />
      ) : (
        <CheckBox
          handleMultiChange={handleMultiChange}
          questionObjVariable={questionObjVariable}
          questionId={questionId}
          surveyAnswers={surveyAnswers}
          isPreview={isPreview}
          surveyAnswersInfo={surveyAnswersInfo}
          handleChangeOtherOptionTextArea={handleChangeOtherOptionTextArea}
          othersOption={othersOption}
        />
      )}
    </>
  );
}

export default CheckBoxContainer;
